<template>
  <div>
    <form @submit.prevent="onCheckForm">
      <app-grid>
        <template #item-1>
          <div class="reservation-grid">
            <div class="reservation-grid__main">
              <div class="reservation-grid__row reservation-grid__row--top">
                <app-form-group label="Hotel" class="form-group--no-indent" required>
                  <app-select
                    @input="onHotelChange"
                    v-model="form.hotel"
                    :options="hotels_options"
                    :clearable="false"
                    placeholder="Choose hotel"
                    :error="$v.form.hotel.$error"
                    :readonly="form.reservation_rooms.length !== 0"
                  />
                  <template #error>
                    <div v-if="$v.form.hotel.$dirty && !$v.form.hotel.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Check-in" class="form-group--no-indent" required>
                  <app-date-picker
                    @input="onSetCheckIn"
                    v-model="form.check_in"
                    placeholder="Check-in"
                    :error="$v.form.check_in.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.check_in.$dirty && !$v.form.check_in.required">Required field</div>
                    <div v-if="$v.form.check_in.$dirty && !$v.form.check_in.minValue">
                      Date cannot be less than the current date
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Check-out" class="form-group--no-indent" required>
                  <app-date-picker
                    v-model="form.check_out"
                    placeholder="Check-out"
                    :readonly="!form.check_in"
                    :default-value="form.check_in"
                    ref="check_out"
                    :error="$v.form.check_out.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.check_out.$dirty && !$v.form.check_out.required">Required field</div>
                    <div v-if="$v.form.check_out.$dirty && !$v.form.check_out.minValue">
                      Date cannot be less than the check-in date
                    </div>
                  </template>
                </app-form-group>
                <app-form-group label="Nights" class="form-group--no-indent" disabled>
                  <app-input :value="calcNumberOfNights" disabled />
                </app-form-group>
              </div>
              <div class="reservation-grid__row reservation-grid__row--single">
                <app-form-group label="Meal plan" class="form-group--no-indent" required>
                  <app-select
                    v-model="form.meal_plan"
                    :reduce="item => item.code"
                    :options="meal_plans_options"
                    :clearable="false"
                    placeholder="Meal plan"
                    :error="$v.form.meal_plan.$error"
                    :readonly="!form.hotel"
                  />
                  <template #error>
                    <div v-if="$v.form.meal_plan.$dirty && !$v.form.meal_plan.required">Required field</div>
                  </template>
                </app-form-group>
              </div>
            </div>
            <div class="reservation-grid__rooms">
              <app-form-group label="Room" class="form-group--no-indent" label_left>
                <app-select
                  v-model="form.type_of_rooms"
                  :options="rooms_options"
                  :clearable="false"
                  placeholder="Choose rooms"
                  class="select--300"
                  :calculate-position="withPopper"
                  :append-to-body="true"
                  :readonly="!form.hotel"
                />
              </app-form-group>
              <div
                v-if="form.type_of_rooms && form.type_of_rooms.accomodation_types && form.type_of_rooms.accomodation_types.length"
                class="reservation-grid__types-list"
              >
                <app-room-icon
                  @click="onRoomIconClick"
                  v-for="(type, index) in form.type_of_rooms.accomodation_types"
                  :key="index"
                  :accomodation_type="type"
                />
              </div>
            </div>
          </div>
        </template>
        <template #item-2>
          <h3 class="title reservation-grid__title">Added rooms</h3>
          <template v-if="form.reservation_rooms.length">
            <div class="reservation-grid__room-list">
              <div v-for="(room, index) in form.reservation_rooms" :key="index" class="reservation-grid__room">
                <app-hotel-room
                  @onChangeRoomClients="onChangeRoomClients($event, index)"
                  @onRoomDelete="onRoomDelete(index)"
                  :name="room.hotel_room.name"
                  :room_type="room.hotel_room.room_type.name"
                  :accomodation_type="room.accomodation_type"
                  :id="room.hotel_room.id"
                  clearable
                  :count="index + 1"
                />
              </div>
            </div>
          </template>
          <div v-else class="no-results no-results--small no-results--margin">
            <div class="no-results__title">No Results</div>
            <div class="no-results__text">There’s no rooms yet</div>
          </div>
        </template>
      </app-grid>
      <app-cells position="end" :indent="false">
        <app-button @click.native="onFormReset" theme="dark">Reset</app-button>
        <app-button
          :disabled="$v.form.$error || !form.reservation_rooms.length"
          type="submit"
          ref="submit_create"
        >
          Calculate
        </app-button>
      </app-cells>
    </form>
    <template v-if="price">
      <app-cells><h2 class="title">Calculation result</h2></app-cells>
      <div v-if="price === 0" class="attention-info">
        <img src="@/assets/img/warning-icon.svg" alt="">
        <p>Hotel price not found! Contact hotel database admin</p>
      </div>
      <app-form-group label="Estimated price">
        <app-input :value="`${price.toLocaleString()} USD`" class="input--150" readonly />
      </app-form-group>
    </template>
  </div>
</template>

<script>
import AppHotelRoom from '@/components/AppHotelRoom.vue'
import AppRoomIcon from '@/components/AppRoomIcon.vue'
import { helpers, required } from 'vuelidate/lib/validators'
import { getHotelsListSearch, getHotelsRooms, postHotelsPriceCheck } from '@/http'
import { createPopper } from '@popperjs/core'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9])?$/i)

export default {
  name: 'HotelsCheckPrice',
  components: { AppRoomIcon, AppHotelRoom },
  data() {
    return {
      form: {
        reservation_rooms: []
      },
      hotels_options: [],
      meal_plans_options: [],
      rooms_options: [],
      placement: 'bottom',
      price: null,
    }
  },
  validations: {
    form: {
      hotel: { required },
      check_in: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
      check_out: {
        required,
        minValue(val) {
          return val ? new Date(val).setHours(0, 0, 0, 0) > new Date(new Date(this.form.check_in).setHours(0, 0, 0, 0)) : true
        }
      },
      meal_plan: { required },
    },
  },
  created() {
    getHotelsListSearch({ active: true }).then(res => { this.hotels_options = res.data })
  },
  watch: {
    form: {
      handler() {
        if (this.price) this.price = null
      },
      deep: true
    }
  },
  computed: {
    calcNumberOfNights() {
      if (this.form.check_in && this.form.check_out) {
        const date1 = new Date(this.form.check_in)
        const date2 = new Date(this.form.check_out)
        const timeDiff = date2.getTime() - date1.getTime()
        return Math.ceil(timeDiff / (1000 * 3600 * 24)) < 0 ? 0 : Math.ceil(timeDiff / (1000 * 3600 * 24))
      } else {
        return ''
      }
    },
  },
  methods: {
    onFormReset() {
      this.form = { reservation_rooms: [] }
      this.$v.form.$reset()
      this.price = null
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit_create.preload = true
      postHotelsPriceCheck(this.normalizeForm())
        .finally(() => { this.$refs.submit_create.preload = false })
        .then(res => {
          this.price = res.data
        })
    },
    normalizeForm() {
      const form = {}

      form.hotel = this.form.hotel.id
      form.check_in = this.form.check_in
      form.check_out = this.form.check_out
      form.meal_plan = this.form.meal_plan
      form.rooms = this.form.reservation_rooms.map(item => ({ hotel_room: item.hotel_room.id, accomodation_type: item.accomodation_type.code }))

      return form
    },
    onHotelChange() {
      this.meal_plans_options = this.form.hotel.meal_plans
      delete this.form.type_of_rooms
      delete this.form.meal_plan
      getHotelsRooms(this.form.hotel.id).then(res => { this.rooms_options = res.data })
    },
    onSetCheckIn() {
      this.$refs.check_out.$children[0].focus()
    },
    onRoomIconClick(type) {
      this.form.reservation_rooms.push({ hotel_room: this.form.type_of_rooms, accomodation_type: type, clients: [] })
    },
    onChangeRoomClients(clients, index) {
      this.form.reservation_rooms[index].clients = clients
    },
    onRoomDelete(index) {
      this.form.reservation_rooms.splice(index, 1)
    },
    withPopper(dropdownList, component, {width}) {
      dropdownList.style.width = width
      createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0],
              adaptive: true
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          },
        ]
      })
    }
  }
}
</script>

<style lang="sass" scoped>
// ------------------------- reservation-grid
.reservation-grid__row
  display: grid
  gap: 20px
  margin-bottom: 20px

  &:last-child
    margin-bottom: 0

.reservation-grid__main
  padding-bottom: 20px
  border-bottom: 1px solid #ebebeb

.reservation-grid__row--top
  grid-template-columns: 1fr 140px 140px 65px

.reservation-grid__row--quartet
  grid-template-columns: 125px 200px 125px 178px
  align-items: center

.reservation-grid__row--single
  grid-template-columns: 206px 100px

.reservation-grid__room-list
  margin-bottom: 20px

.reservation-grid__rooms
  padding-top: 20px

.reservation-grid__types-list
  display: flex
  justify-content: flex-start
  align-items: center
  flex-wrap: wrap
  gap: 10px
  padding-top: 20px

.reservation-grid__room
  margin-bottom: 15px

.reservation-grid__title
  margin-bottom: 15px

.reservation-grid__clients
  padding-top: 15px

.reservation-grid__clients-list
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 10px 20px
  margin-top: 10px
  padding: 20px
  border: 1px solid #ebebeb
  border-radius: 9px
  background-color: #fdfdfd

.reservation-grid__client
  display: flex
  align-items: center
  gap: 4px
  width: 145px

  span
    padding-top: 2px
</style>
